import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Download, ImageIcon, Lock, MailCheck, Trash2, UserPlus, Users, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { EVENTS } from "@/lib/wedding-data";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const eventName = (k) => EVENTS.find((e) => e.key === k)?.name || k;

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem("viyush-admin-key") || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("rsvps");
  const [rsvpData, setRsvpData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [invitees, setInvitees] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [invForm, setInvForm] = useState({ name: "", email: "" });
  const [sending, setSending] = useState(false);

  const load = async (k) => {
    try {
      const headers = { "X-Admin-Key": k };
      const [r, p, inv] = await Promise.all([
        axios.get(`${API}/admin/rsvps`, { headers }),
        axios.get(`${API}/admin/photos`, { headers }),
        axios.get(`${API}/admin/invitees`, { headers }),
      ]);
      setRsvpData(r.data);
      setPhotos(p.data.photos);
      setInvitees(inv.data.invitees);
      setPendingCount(inv.data.pending_count);
      setAuthed(true);
      localStorage.setItem("viyush-admin-key", k);
    } catch (e) {
      setAuthed(false);
      localStorage.removeItem("viyush-admin-key");
      if (k) toast.error("Incorrect passcode.");
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moderate = async (id, action) => {
    try {
      await axios.post(
        `${API}/admin/photos/${id}/${action}`,
        {},
        { headers: { "X-Admin-Key": key } }
      );
      toast.success(
        action === "approve" ? "Photo is live on the wall." : "Photo removed."
      );
      load(key);
    } catch {
      toast.error("Action failed. Please try again.");
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/rsvps/export`, {
        headers: { "X-Admin-Key": key },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "viyush-guest-list.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed. Please try again.");
    }
  };

  const addInvitee = async (e) => {
    e.preventDefault();
    if (!invForm.name.trim() || !invForm.email.trim()) {
      toast.error("Add both a name and an email.");
      return;
    }
    try {
      await axios.post(`${API}/admin/invitees`, invForm, {
        headers: { "X-Admin-Key": key },
      });
      toast.success(`${invForm.name} added to the invite list.`);
      setInvForm({ name: "", email: "" });
      load(key);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Could not add guest - check the email address.");
    }
  };

  const removeInvitee = async (id) => {
    try {
      await axios.delete(`${API}/admin/invitees/${id}`, {
        headers: { "X-Admin-Key": key },
      });
      load(key);
    } catch {
      toast.error("Could not remove guest.");
    }
  };

  const sendReminders = async () => {
    if (!window.confirm(`Send a reminder email to ${pendingCount} guests who haven't RSVP'd?`)) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/reminders/send`,
        {},
        { headers: { "X-Admin-Key": key } }
      );
      toast.success(`Reminders sent to ${res.data.sent} guests.`);
    } catch {
      toast.error("Could not send reminders.");
    } finally {
      setSending(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6">
        <div className="gold-frame w-full max-w-md rounded-2xl bg-white/70 border border-[#D4AF37]/40 p-10 text-center">
          <Lock className="h-6 w-6 mx-auto text-[#D4AF37]" />
          <h1 className="mt-4 font-serif text-3xl text-[#5D171E] tracking-tight">
            Hosts only
          </h1>
          <p className="mt-2 text-sm text-[#1A1A1A]/60">
            Enter the host passcode to view RSVPs and moderate guest photos.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              load(key);
            }}
            className="mt-6 space-y-4"
          >
            <Input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Passcode"
              data-testid="admin-passcode-input"
              className="bg-white/70 border-[#D4AF37]/40 text-center focus-visible:ring-[#5D171E]"
            />
            <Button
              type="submit"
              data-testid="admin-login-button"
              className="w-full rounded-full bg-[#5D171E] text-[#FDFBF7] text-xs uppercase tracking-[0.25em] py-6 hover:bg-[#471016] transition-colors duration-300"
            >
              Enter
            </Button>
          </form>
          <a
            href="/"
            data-testid="admin-back-link"
            className="mt-6 inline-block text-xs uppercase tracking-[0.2em] text-[#5D171E]/60 hover:text-[#5D171E] transition-colors duration-300"
          >
            Back to the invitation
          </a>
        </div>
      </div>
    );
  }

  const pending = photos.filter((p) => p.status === "pending");
  const approved = photos.filter((p) => p.status === "approved");
  const stats = rsvpData?.stats || {};

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-dashboard">
      <header className="bg-[#471016] text-[#FDFBF7]">
        <div className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#E1C699]">
              Host Dashboard
            </p>
            <h1 className="mt-1 font-serif text-3xl tracking-tight">
              Vishi <span className="italic text-[#E1C699]">&</span> Piyush
            </h1>
          </div>
          <a
            href="/"
            data-testid="admin-back-link-top"
            className="text-xs uppercase tracking-[0.2em] text-[#E1C699]/80 hover:text-[#E1C699] transition-colors duration-300"
          >
            View Site
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: "Parties Attending", value: stats.attending ?? 0, id: "attending" },
            { label: "Total Guests", value: stats.guest_total ?? 0, id: "guests" },
            { label: "Regrets", value: stats.declined ?? 0, id: "declined" },
            { label: "Photos to Review", value: pending.length, id: "pending" },
          ].map((s) => (
            <div
              key={s.id}
              data-testid={`admin-stat-${s.id}`}
              className="rounded-2xl border border-[#D4AF37]/30 bg-white/60 p-6"
            >
              <p className="font-serif text-4xl text-[#5D171E]">{s.value}</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#1A1A1A]/60">
                {s.label}
              </p>
            </div>
          ))}
        </div>

        <div className="flex gap-3 mb-8">
          <button
            onClick={() => setTab("rsvps")}
            data-testid="admin-tab-rsvps"
            className={`rounded-full px-6 py-3 text-xs uppercase tracking-[0.2em] border transition-colors duration-300 ${
              tab === "rsvps"
                ? "bg-[#5D171E] text-[#FDFBF7] border-[#5D171E]"
                : "text-[#5D171E] border-[#D4AF37]/50 hover:border-[#5D171E]"
            }`}
          >
            <Users className="inline h-3.5 w-3.5 mr-2" />
            RSVPs
          </button>
          <button
            onClick={() => setTab("photos")}
            data-testid="admin-tab-photos"
            className={`rounded-full px-6 py-3 text-xs uppercase tracking-[0.2em] border transition-colors duration-300 ${
              tab === "photos"
                ? "bg-[#5D171E] text-[#FDFBF7] border-[#5D171E]"
                : "text-[#5D171E] border-[#D4AF37]/50 hover:border-[#5D171E]"
            }`}
          >
            <ImageIcon className="inline h-3.5 w-3.5 mr-2" />
            Photo Moderation {pending.length > 0 && `(${pending.length})`}
          </button>
          <button
            onClick={() => setTab("guests")}
            data-testid="admin-tab-guests"
            className={`rounded-full px-6 py-3 text-xs uppercase tracking-[0.2em] border transition-colors duration-300 ${
              tab === "guests"
                ? "bg-[#5D171E] text-[#FDFBF7] border-[#5D171E]"
                : "text-[#5D171E] border-[#D4AF37]/50 hover:border-[#5D171E]"
            }`}
          >
            <MailCheck className="inline h-3.5 w-3.5 mr-2" />
            Invites & Reminders {pendingCount > 0 && `(${pendingCount} pending)`}
          </button>
        </div>

        {tab === "rsvps" && (
          <div className="space-y-4" data-testid="admin-rsvp-list">
            <div className="flex justify-end mb-2">
              <button
                onClick={downloadCsv}
                data-testid="admin-export-csv"
                className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 text-[#5D171E] text-xs uppercase tracking-[0.2em] px-6 py-3 hover:border-[#5D171E] transition-colors duration-300"
              >
                <Download className="h-3.5 w-3.5" /> Download Guest List (CSV)
              </button>
            </div>
            {(rsvpData?.rsvps || []).length === 0 && (
              <p className="text-sm text-[#1A1A1A]/60">No RSVPs yet.</p>
            )}
            {(rsvpData?.rsvps || []).map((r, i) => (
              <div
                key={r.id || i}
                data-testid={`admin-rsvp-row-${i}`}
                className="rounded-2xl border border-[#D4AF37]/30 bg-white/60 p-6 flex flex-col md:flex-row md:items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-serif text-xl text-[#1A1A1A]">{r.name}</p>
                    <span
                      className={`text-[10px] uppercase tracking-[0.2em] rounded-full px-3 py-1 ${
                        r.attending
                          ? "bg-[#5D171E] text-[#E1C699]"
                          : "bg-[#1A1A1A]/10 text-[#1A1A1A]/60"
                      }`}
                    >
                      {r.attending ? "Accepts" : "Declines"}
                    </span>
                    {r.attending && (
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">
                        Party of {r.guests_count || 1}
                      </span>
                    )}
                  </div>
                  {r.events?.length > 0 && (
                    <p className="mt-1 text-xs uppercase tracking-[0.15em] text-[#1A1A1A]/50">
                      {r.events.map(eventName).join(" · ")}
                    </p>
                  )}
                  {r.message && (
                    <p className="mt-2 text-sm text-[#1A1A1A]/70 italic">
                      “{r.message}”
                    </p>
                  )}
                </div>
                <div className="text-xs text-[#1A1A1A]/50 md:text-right">
                  {r.email && <p>{r.email}</p>}
                  <p className="mt-1">
                    {r.created_at
                      ? new Date(r.created_at).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                        })
                      : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "photos" && (
          <div data-testid="admin-photo-moderation">
            <h2 className="text-xs uppercase tracking-[0.3em] text-[#5D171E] mb-6">
              Awaiting Approval ({pending.length})
            </h2>
            {pending.length === 0 ? (
              <p className="text-sm text-[#1A1A1A]/60 mb-12">
                Nothing waiting - the queue is clear.
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
                {pending.map((p, i) => (
                  <div
                    key={p.id}
                    data-testid={`admin-pending-photo-${i}`}
                    className="rounded-2xl border border-[#D4AF37]/30 bg-white/60 overflow-hidden"
                  >
                    <img
                      src={`${BACKEND}/api/photos/file/${p.storage_path}`}
                      alt={p.uploader_name ? `From ${p.uploader_name}` : "Guest upload"}
                      className="w-full aspect-square object-cover"
                    />
                    <div className="p-4">
                      <p className="text-xs uppercase tracking-[0.15em] text-[#1A1A1A]/60 truncate">
                        {p.uploader_name || "Anonymous"}
                      </p>
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => moderate(p.id, "approve")}
                          data-testid={`admin-approve-${i}`}
                          className="flex-1 rounded-full bg-[#5D171E] text-[#FDFBF7] text-[10px] uppercase tracking-[0.15em] py-2.5 hover:bg-[#471016] transition-colors duration-300"
                        >
                          <Check className="inline h-3 w-3 mr-1" /> Approve
                        </button>
                        <button
                          onClick={() => moderate(p.id, "reject")}
                          data-testid={`admin-reject-${i}`}
                          className="flex-1 rounded-full border border-[#5D171E]/30 text-[#5D171E] text-[10px] uppercase tracking-[0.15em] py-2.5 hover:bg-[#5D171E]/5 transition-colors duration-300"
                        >
                          <X className="inline h-3 w-3 mr-1" /> Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <h2 className="text-xs uppercase tracking-[0.3em] text-[#5D171E] mb-6">
              Live on the Wall ({approved.length})
            </h2>
            {approved.length === 0 ? (
              <p className="text-sm text-[#1A1A1A]/60">No approved photos yet.</p>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {approved.map((p, i) => (
                  <img
                    key={p.id}
                    src={`${BACKEND}/api/photos/file/${p.storage_path}`}
                    alt={p.uploader_name ? `From ${p.uploader_name}` : "Approved guest photo"}
                    data-testid={`admin-approved-photo-${i}`}
                    className="w-full aspect-square object-cover rounded-xl border border-[#D4AF37]/30"
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "guests" && (
          <div data-testid="admin-guests-tab" className="grid lg:grid-cols-2 gap-10">
            <div>
              <h2 className="text-xs uppercase tracking-[0.3em] text-[#5D171E] mb-6">
                Invite List
              </h2>
              <form
                onSubmit={addInvitee}
                className="rounded-2xl border border-[#D4AF37]/30 bg-white/60 p-6 mb-6 space-y-4"
              >
                <Input
                  value={invForm.name}
                  onChange={(e) => setInvForm({ ...invForm, name: e.target.value })}
                  placeholder="Guest name"
                  data-testid="admin-invitee-name"
                  className="bg-white/70 border-[#D4AF37]/40 focus-visible:ring-[#5D171E]"
                />
                <Input
                  type="email"
                  value={invForm.email}
                  onChange={(e) => setInvForm({ ...invForm, email: e.target.value })}
                  placeholder="Guest email"
                  data-testid="admin-invitee-email"
                  className="bg-white/70 border-[#D4AF37]/40 focus-visible:ring-[#5D171E]"
                />
                <Button
                  type="submit"
                  data-testid="admin-invitee-add"
                  className="w-full rounded-full bg-[#5D171E] text-[#FDFBF7] text-xs uppercase tracking-[0.2em] py-5 hover:bg-[#471016] transition-colors duration-300"
                >
                  <UserPlus className="inline h-3.5 w-3.5 mr-2" /> Add to Invite List
                </Button>
              </form>

              <div className="space-y-3">
                {invitees.length === 0 && (
                  <p className="text-sm text-[#1A1A1A]/60">
                    No invited guests yet - add your guest list above to track
                    who has responded.
                  </p>
                )}
                {invitees.map((inv, i) => (
                  <div
                    key={inv.id}
                    data-testid={`admin-invitee-row-${i}`}
                    className="rounded-2xl border border-[#D4AF37]/30 bg-white/60 px-5 py-4 flex items-center gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-lg text-[#1A1A1A] truncate">{inv.name}</p>
                      <p className="text-xs text-[#1A1A1A]/50 truncate">{inv.email}</p>
                    </div>
                    <span
                      data-testid={`admin-invitee-status-${i}`}
                      className={`text-[10px] uppercase tracking-[0.2em] rounded-full px-3 py-1 ${
                        inv.has_rsvpd
                          ? "bg-[#5D171E] text-[#E1C699]"
                          : "bg-[#D4AF37]/20 text-[#5D171E]"
                      }`}
                    >
                      {inv.has_rsvpd ? "RSVP'd" : "Pending"}
                    </span>
                    <button
                      onClick={() => removeInvitee(inv.id)}
                      data-testid={`admin-invitee-remove-${i}`}
                      className="text-[#1A1A1A]/40 hover:text-[#5D171E] transition-colors duration-300"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-xs uppercase tracking-[0.3em] text-[#5D171E] mb-6">
                Gentle Reminders
              </h2>
              <div className="rounded-2xl bg-[#5D171E] text-[#FDFBF7] p-8">
                <p className="font-serif text-4xl" data-testid="admin-pending-count">
                  {pendingCount}
                </p>
                <p className="mt-1 text-sm text-[#FDFBF7]/70">
                  {pendingCount === 1 ? "guest hasn't" : "guests haven't"} RSVP'd yet
                </p>
                <p className="mt-6 text-sm text-[#FDFBF7]/70 leading-relaxed">
                  One tap sends a tasteful reminder email to every pending guest,
                  with the dates, venue and a link back to RSVP. Suggested
                  timing: <span className="text-[#E1C699]">January 17, 2027</span> -
                  one week before the wedding.
                </p>
                <button
                  onClick={sendReminders}
                  disabled={sending || pendingCount === 0}
                  data-testid="admin-send-reminders"
                  className="mt-8 w-full rounded-full bg-[#D4AF37] text-[#5D171E] text-xs uppercase tracking-[0.25em] py-4 font-medium hover:bg-[#E1C699] transition-colors duration-300 disabled:opacity-50"
                >
                  {sending ? "Sending..." : `Send Reminders to ${pendingCount} Pending`}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
